/**
 * Cross-lab range normalization for blood test results.
 *
 * Direct port of normalizeAggregatedResults() and parseResultValue()
 * from src/util/GenericUtil.js.
 * No new logic — same normalization math.
 *
 * SYNC WARNING: This file must stay in sync with normalization.py (Python port).
 * Both files contain identical logic. When modifying this file, update normalization.py to match.
 */

import { UnitConverter, normalizeUnit } from './unit-conversion.mjs';

/**
 * Parse a result value, stripping < or > prefixes.
 * @param {*} raw
 * @returns {number}
 */
function resultNumberText(raw) {
  if (raw == null) return null;
  let text = String(raw).trim();
  if (!text) return null;

  // Comparator prefixes: "<", ">=", "> OR = 40", "LESS THAN 5"
  text = text.replace(/^(?:<|>|\u2264|\u2265)\s*(?:or)?\s*=?\s*/i, '');
  text = text.replace(/^(?:less|greater)\s+than(?:\s+or\s+equal(?:\s+to)?)?\s*/i, '');

  // Ratios and titers have no single magnitude - don't take the numerator.
  if (/^\d+\s*:\s*\d+/.test(text)) return null;

  const match = text.match(/^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d+)?(?:[eE][+-]?\d+)?/);
  if (!match || !/\d/.test(match[0])) return null;
  return match[0];
}

export function parseResultValue(raw) {
  if (typeof raw === 'number') return raw;
  const number = resultNumberText(raw);
  if (number === null) return NaN;
  return Number(number.replace(/,/g, ''));
}

/**
 * Count decimal places in a result for precision preservation.
 */
export function resultPrecision(raw) {
  const number = resultNumberText(raw);
  if (number === null) return 0;
  const idx = number.indexOf('.');
  return idx >= 0 ? number.length - idx - 1 : 0;
}

/**
 * Normalize an aggregated test's historical results to a common reference range.
 *
 * Takes a test object with a `results` array (each entry has result, lab_low, lab_high, units)
 * and normalizes all results to the most recent test's lab range using:
 * 1. Unit conversion (if units differ between tests)
 * 2. Range normalization (if lab ranges differ between labs)
 *
 * @param {Object} aggregatedTest - Must have: test_name, lab_low, lab_high, units, results[]
 * @returns {Object} Same structure with normalized result values
 */
export function normalizeAggregatedResults(aggregatedTest) {
  if (!aggregatedTest.results || aggregatedTest.results.length <= 1) return aggregatedTest;

  if (aggregatedTest.lab_low == null || aggregatedTest.lab_high == null) return aggregatedTest;
  const targetLow = Number(aggregatedTest.lab_low);
  const targetHigh = Number(aggregatedTest.lab_high);
  const targetUnits = aggregatedTest.units;

  if (!Number.isFinite(targetLow) || !Number.isFinite(targetHigh)) return aggregatedTest;

  const normalizedResults = aggregatedTest.results.map(r => {
    let convertedResult = parseResultValue(r.result);
    const decimals = resultPrecision(r.result);

    if (r.lab_low == null || r.lab_high == null) return { ...r, result: convertedResult };
    let rLow = Number(r.lab_low);
    let rHigh = Number(r.lab_high);

    // Step 1: Unit conversion (if units differ)
    if (r.units && targetUnits && normalizeUnit(r.units) !== normalizeUnit(targetUnits)) {
      const conv = UnitConverter.convert(aggregatedTest.test_name, convertedResult, r.units, targetUnits);
      if (conv !== null) {
        const convLow = UnitConverter.convert(aggregatedTest.test_name, rLow, r.units, targetUnits);
        const convHigh = UnitConverter.convert(aggregatedTest.test_name, rHigh, r.units, targetUnits);
        convertedResult = conv;
        if (convLow !== null) rLow = convLow;
        if (convHigh !== null) rHigh = convHigh;
      }
    }

    if (!Number.isFinite(rLow) || !Number.isFinite(rHigh)) return { ...r, result: convertedResult };
    // Step 2: Range normalization (if lab ranges differ)
    if (rLow === targetLow && rHigh === targetHigh) return { ...r, result: convertedResult };

    const normalized = UnitConverter.normalizeToRange(convertedResult, rLow, rHigh, targetLow, targetHigh);
    if (normalized == null) return { ...r, result: convertedResult };
    return { ...r, result: Number(normalized.toFixed(decimals)) };
  });

  return { ...aggregatedTest, results: normalizedResults };
}
